import { StyleSheet, css } from 'aphrodite'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import BlurbSection from '../components/BlurbSection'
import HeroSection from '../components/HeroSection'
import LandingCard from '../components/LandingCard'
import LandingSection from '../components/LandingSection'
import TitleBlurbSection from '../components/TitleBlurbSection'
import LandingFooter from '../components/navigation/LandingFooter'
import LandingHeader from '../components/navigation/LandingHeader'

export default function CollaborativeGovernancePage() {
  const navigate = useNavigate()

  const handleJoinCommunityClick = () => {
    window.scrollTo(0, 0)
    navigate('/register')
  }

  return (
    <div className={css(styles.container)}>
      <LandingHeader />
      <HeroSection
        subtitle="Collaborative Governance"
        title="SHARED GOVERNANCE, SHARED SUCCESS"
        description="Digital governance is built on a core belief: Stakeholders shape the ecosystem. Rather than imposing top-down rules, the governance structure of the Ecosystem evolves through active engagement with all stakeholders who use it every day."
        imageSrc="/collab_hero2.webp"
        imageAlt="A vision for a digital future"
      />
      <BlurbSection>
        Rules are not written <i>for</i> stakeholders. They are written{' '}
        <i>by</i> stakeholders.
      </BlurbSection>

      <section className={css(styles.coCreationSection)}>
        <div className={css(styles.coCreationContent)}>
          <h2 className={css(styles.coCreationTitle)}>
            COLLABORATION IN ACTION
          </h2>
          <p className={css(styles.coCreationDescription)}>
            The architects of this Ecosystem act as collaborators, not
            controllers. Governance is developed as a flexible tool – one that
            evolves continuously. As stakeholders contribute insight, share
            feedback, and engage with the Ecosystem, the structure adapts in
            real time.
          </p>
          <p className={css(styles.coCreationDescription)}>
            This is an evolving system where data, decisions, and designs inform
            one another continuously. Governance and technology co-evolve,
            keeping the experience relevant, inclusive, and important.
          </p>
          <button
            className={css(styles.getStartedButton)}
            onClick={handleJoinCommunityClick}
          >
            Join Community
          </button>
        </div>
        <div className={css(styles.coCreationImageContainer)}>
          <img
            src="/collaboration.webp"
            alt="Co-creation in action"
            className={css(styles.coCreationImage)}
          />
        </div>
      </section>

      <TitleBlurbSection
        title="ROOTED IN SHARED VALUES"
        description="Transparency, Trust, and Collaboration shape the culture of the Dump Truck Ecosystem. These values drive not only how stakeholders participate, but also how decisions are made and frameworks are formed."
      />

      <section className={css(styles.cardSection)}>
        <LandingCard
          number={1}
          title="COLLABORATIVE STANDARDS"
          description="Rules and standards are co-developed with active input and decision-making from all stakeholders in the ecosystem."
          backgroundColor="#D6E6F5"
        />
        <LandingCard
          number={2}
          title="CONTINUOUS IMPROVEMENT"
          description="Feedback loops drive continuous improvement, ensuring the Ecosystem evolves with the stakeholders’ needs."
          backgroundColor="#F8FFD3"
        />
        <LandingCard
          number={3}
          title="STAKEHOLDER-SHAPED ARCHITECTURE"
          description="Stakeholders’ needs shape the architecture, not the other way around, creating a truly responsive and collaborative Ecosystem. "
          backgroundColor="#D3FFEA"
        />
      </section>

      <section className={css(styles.lookingAheadSection)}>
        <div className={css(styles.lookingAheadImageContainer)}>
          <img
            src="/looking_ahead2.webp"
            alt="Looking Ahead"
            className={css(styles.lookingAheadImage)}
            loading="lazy"
          />
        </div>
        <div className={css(styles.lookingAheadContent)}>
          <h2 className={css(styles.lookingAheadTitle)}>LOOKING AHEAD</h2>
          <p className={css(styles.lookingAheadDescription)}>
            As the ecosystem expands, so does its capacity to include more
            voices, ideas, and solutions. Governance at the Digital Ecosystem
            isn't about control — it's about enabling a digital environment
            where shared ownership leads to shared success.
          </p>
          <ul className={css(styles.checklist)}>
            <li className={css(styles.checklistItem)}>
              <img
                src="/Checkmark.png"
                alt="Checkmark"
                className={css(styles.checklistIcon)}
                loading="lazy"
              />
              <span>
                Expanding Participation: Creating more opportunities for users
                to contribute to governance decisions
              </span>
            </li>
            <li className={css(styles.checklistItem)}>
              <img
                src="/Checkmark.png"
                alt="Checkmark"
                className={css(styles.checklistIcon)}
                loading="lazy"
              />
              <span>
                Data-Driven Evolution: Using ecosystem data to inform governance
                improvements
              </span>
            </li>
            <li className={css(styles.checklistItem)}>
              <img
                src="/Checkmark.png"
                alt="Checkmark"
                className={css(styles.checklistIcon)}
                loading="lazy"
              />
              <span>
                Greater Windows of Opportunity: Creating more opportunities for
                stakeholders to contribute to governance decisions
              </span>
            </li>
          </ul>
        </div>
      </section>
      <LandingSection
        title="JOIN THE ECOSYSTEM"
        description="Work smarter with real-time updates, instant data access, and modular, digital templates and workflows. Cut down on paperwork, build trust, and reduce unnecessary driving – all in one Ecosystem."
        imageSrc="/ecosystem2.webp"
        imageAlt="Construction Team"
        buttonText="Join Community"
        testimonialText="Trusted by 10,000+ operators,"
        userCount="contractors and haulers"
        onButtonClick={handleJoinCommunityClick}
      />

      <LandingFooter />
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
  },
  mainContent: {
    flex: 1,
    padding: '80px 16px',
    maxWidth: '1280px',
    margin: '0 auto',
    width: '100%',
    '@media (min-width: 640px)': {
      padding: '80px 24px',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 32px',
    },
  },
  coCreationSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '32px',
    alignItems: 'center',
    padding: '40px 1rem',
    backgroundColor: '#F2F5F7',
    '@media (min-width: 768px)': {
      gap: '40px',
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: '48px',
      padding: '80px 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  coCreationContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    textAlign: 'center',
    paddingBottom: '5rem',
    '@media (min-width: 768px)': {
      gap: '20px',
    },
    '@media (min-width: 1024px)': {
      gap: '24px',
      textAlign: 'left',
    },
  },
  coCreationTitle: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '32px',
    fontWeight: 400,
    color: '#1F3D61',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  coCreationDescription: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#4B5563',
    fontWeight: 400,
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '18px',
    },
  },
  getStartedButton: {
    backgroundColor: '#6DC3FF',
    color: '#1F407E',
    padding: '10px 24px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    alignSelf: 'center',
    width: '50%',
    '@media (min-width: 768px)': {
      padding: '11px 28px',
      borderRadius: '22px',
      fontSize: '15px',
      width: '40%',
    },
    '@media (min-width: 1024px)': {
      padding: '12px 32px',
      borderRadius: '24px',
      fontSize: '16px',
      alignSelf: 'flex-start',
      width: '30%',
    },
  },
  coCreationImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    order: -1,
    '@media (min-width: 1024px)': {
      order: 0,
    },
  },
  coCreationImage: {
    maxWidth: '100%',
    width: '100%',
    height: 'auto',
    borderRadius: '16px',
    '@media (min-width: 768px)': {
      borderRadius: '20px',
      maxWidth: '90%',
    },
    '@media (min-width: 1024px)': {
      borderRadius: '24px',
      maxWidth: '100%',
    },
  },
  cardSection: {
    minHeight: '50vh',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '40px 1rem',
    alignItems: 'stretch',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      gap: '20px',
      padding: '60px 2rem',
      alignItems: 'stretch',
    },
    '@media (min-width: 1024px)': {
      gap: '24px',
      padding: '80px 3rem',
      minHeight: '50vh',
      alignItems: 'stretch',
    },
  },
  lookingAheadSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '32px',
    alignItems: 'center',
    padding: '40px 1rem',
    '@media (min-width: 768px)': {
      gap: '40px',
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: '48px',
      padding: '80px 3rem',
      maxHeight: '200vh',
    },
    '@media (min-width: 1280px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: '48px',
      padding: '80px 3rem',
      maxHeight: '200vh',
    },
  },
  lookingAheadImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    order: -1,
    '@media (min-width: 1024px)': {
      order: 0,
    },
  },
  lookingAheadImage: {
    maxWidth: '100%',
    width: '100%',
    height: 'auto',
    maxHeight: '80vh',
    objectFit: 'contain',
    borderRadius: '16px',
  },
  lookingAheadContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      gap: '20px',
    },
    '@media (min-width: 1024px)': {
      gap: '24px',
      textAlign: 'left',
    },
  },
  lookingAheadTitle: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '32px',
    fontWeight: 400,
    color: '#102E65',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  lookingAheadDescription: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#4B5563',
    fontWeight: 400,
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '18px',
    },
  },
  checklist: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    '@media (min-width: 768px)': {
      gap: '14px',
    },
    '@media (min-width: 1024px)': {
      gap: '16px',
    },
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    paddingBottom: '12px',
    borderBottom: '1px solid #E8E8E8',
    color: '#4B5563',
    fontSize: '14px',
    textAlign: 'left',
    '@media (min-width: 768px)': {
      gap: '14px',
      paddingBottom: '14px',
      fontSize: '15px',
    },
    '@media (min-width: 1024px)': {
      gap: '16px',
      paddingBottom: '16px',
      fontSize: '16px',
    },
  },
  checklistIcon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    '@media (min-width: 768px)': {
      width: '24px',
      height: '24px',
    },
    '@media (min-width: 1024px)': {
      width: '28px',
      height: '28px',
    },
  },
})
